import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Hidden,
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import { Edit, DeleteOutline, AddBox, Search, Assignment } from "@material-ui/icons";

import clsx from "clsx";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TenantModal from "../../components/T_tenantModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import T_tenantsServersDrawer from "../../components/T_tenantsServersDrawer";



const reducer = (state, action) => {
  if (action.type === "LOAD_TENTANTS") {
    const tenants = action.payload;
    const newTenants = [];

    tenants.forEach((tenant) => {
      const tenantIndex = state.findIndex((q) => q.id === tenant.id);
      if (tenantIndex !== -1) {
        state[tenantIndex] = tenant;
      } else {
        newTenants.push(tenant);
      }
    });

    return [...state, ...newTenants];
  }

  if (action.type === "UPDATE_TENTANTS") {
    const tenant = action.payload;
    const tenantIndex = state.findIndex((q) => q.id === tenant.id);

    if (tenantIndex !== -1) {
      state[tenantIndex] = tenant;
      return [...state];
    } else {
      return [tenant, ...state];
    }
  }

  if (action.type === "DELETE_TENTANTS") {
    const tenantId = action.payload;

    const tenantIndex = state.findIndex((q) => q.id === tenantId);
    if (tenantIndex !== -1) {
      state.splice(tenantIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};
const drawerWidth = 520;

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    marginLeft: "10px",
    borderRadius: "10px",
  },

  mainWrapper: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    // marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    marginRight: drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    // marginRight: 0,
  },
  tableRow: {
    borderBotton: "1 solid silver",
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "#D0F0C0",
      // "& > .MuiTableCell-root": {
      //   color: "white"
      // }
    }
  }
}));


const Tenants = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [tenants, dispatch] = useReducer(reducer, []);
  const [selectedTenants, setSelectedTenants] = useState(null);
  const [tenantsModalOpen, setTenantModalOpen] = useState(false);
  const [deletingTenants, setDeletingTenants] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tenant, setTenant] = useState({});
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTenants = async () => {
        try {
          const { data } = await api.get("/tenants/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_TENTANTS", payload: data.tenants });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchTenants();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("tenant", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TENTANTS", payload: data.tenant });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_TENTANTS",
          payload: +data.tenantId,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenTenantModal = () => {
    setSelectedTenants(null);
    setTenantModalOpen(true);
  };

  const handleCloseTenantModal = () => {
    setSelectedTenants(null);
    setTenantModalOpen(false);
  };

  const handleEditTenants = (tenant) => {
    setSelectedTenants(tenant);
    setTenantModalOpen(true);
  };

  const handleDeleteTenants = async (tenantId) => {
    try {
      await api.delete(`/tenants/${tenantId}`);
      toast.success(i18n.t("global.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTenants(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleDrawerOpen = (tenant, tenantsId) => {
    setTenant(tenant);
    setDrawerOpen(true);
    setSelectedTenant(tenantsId);
  };


  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedTenant(0);
    setTenant(0)
  };


  return (
    <div className={classes.root} id="drawer-container">
    <MainContainer>
      <T_tenantsServersDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        tenant={tenant}
        loading={loading}
      />
      <ConfirmationModal
        title={
          deletingTenants &&
          `${i18n.t("global.deleteTitle")} ${deletingTenants.shortcut
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTenants(deletingTenants.id)}
      >
        {i18n.t("global.deleteMessage")}
      </ConfirmationModal>
      <TenantModal
        open={tenantsModalOpen}
        onClose={handleCloseTenantModal}
        aria-labelledby="form-dialog-title"
        tenantId={selectedTenants && selectedTenants.id}
      ></TenantModal>
      <MainHeader>
        <Title>{i18n.t("tenant.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Hidden mdDown>
            <TextField
              placeholder={i18n.t("global.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Hidden>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenTenantModal}
          >
            <AddBox /> <Hidden mdDown>{i18n.t("global.buttons.add")}</Hidden>
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Hidden lgUp>
        <TextField
          placeholder={i18n.t("global.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: "gray" }} />
              </InputAdornment>
            ),
          }}
        />
        <br />
      </Hidden>
      <Paper
        variant="outlined"
        onScroll={handleScroll}
        style={{ overflow: 'auto', paddingRight: 20 }}
        className={clsx(`${classes.mainWrapper} ${classes.mainPaper}`, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <Table size="small" sx={{ minWidth: 650 }} aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                #ID
              </TableCell>
              <TableCell align="center">
                {i18n.t("global.name")}
              </TableCell>
              <TableCell align="center">
                Whatsapp
              </TableCell>
              <TableCell align="center">
                E-mail
              </TableCell>
              <TableCell align="center">
                {i18n.t("global.description")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("global.servers")}
              </TableCell>
              <TableCell align="center">
                Status
              </TableCell>
              <TableCell align="center">
                {i18n.t("global.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {tenants.map((tenant) => (
                <TableRow
                  key={tenant.id}
                  selected={selectedId === tenant.id}
                  className={classes.tableRow}
                >
                  <TableCell align="center">#{tenant.id}</TableCell>
                  <TableCell align="center">{tenant.nickname}<br /> {tenant.name}</TableCell>
                  <TableCell align="center">{tenant.whatsapp}</TableCell>
                  <TableCell align="center">{tenant.email}</TableCell>
                  <TableCell align="center">{tenant.description}</TableCell>

                  <TableCell align="center">{tenant.countServer}</TableCell>
                  <TableCell align="center">{tenant.status ? "✅" : "❌"}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditTenants(tenant)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setConfirmModalOpen(true);
                        setDeletingTenants(tenant);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>

                  </TableCell>
                  <TableCell align="center">

                    <IconButton
                      size="small"
                      onClick={() => { setSelectedId(tenant.id); handleDrawerOpen(tenant, tenant.id) }}
                    >
                      <Assignment style={{ color: "RoyalBlue" }} />
                    </IconButton>

                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
    </div>
  );
};

export default Tenants;
